import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Briefcase, Mail, Lock, ArrowLeft, User, Users, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      const user = await login(formData.email, formData.password);
      toast.success('Welcome back!');
      const from = location.state?.from?.pathname || (user.user_type === 'trainer' ? '/trainer/dashboard' : '/corporate/dashboard');
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="login-page">
      <SEO title="Sign In" description="Sign in to Training Solutions to manage your training profile, apply to requirements, or hire expert trainers." path="/login" />
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div className="mb-10">
            <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </Link>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-white" />
              </div>
              <span className="font-outfit font-bold text-2xl text-slate-900">Training Solutions</span>
            </div>
            <h2 className="font-outfit text-3xl font-bold text-slate-900 mt-8">
              Welcome back
            </h2>
            <p className="mt-2 text-slate-500">
              Sign in to your account to continue
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
              <div className="mt-2 relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 h-12 border-slate-200 focus:ring-2 focus:ring-blue-500"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
              <div className="mt-2 relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 h-12 border-slate-200 focus:ring-2 focus:ring-blue-500"
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-lg"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </div>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="mt-8">
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-3 text-slate-400">New to Training Solutions?</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Link
                to="/register?type=trainer"
                className="flex flex-col items-center gap-2 p-4 border-2 border-slate-200 rounded-xl hover:border-blue-500 hover:bg-blue-50/50 transition-all group"
                data-testid="join-as-trainer-btn"
              >
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-sm font-semibold text-slate-900">Join as Trainer</span>
                <span className="text-xs text-slate-500 text-center leading-tight">Offer your expertise</span>
              </Link>
              <Link
                to="/register?type=corporate"
                className="flex flex-col items-center gap-2 p-4 border-2 border-slate-200 rounded-xl hover:border-emerald-500 hover:bg-emerald-50/50 transition-all group"
                data-testid="join-as-company-btn"
              >
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                  <Users className="w-5 h-5 text-emerald-600" />
                </div>
                <span className="text-sm font-semibold text-slate-900">Join as Company</span>
                <span className="text-xs text-slate-500 text-center leading-tight">Hire expert trainers</span>
              </Link>
            </div>
          </div>

          <p className="mt-4 text-center">
            <Link to="/forgot-password" className="text-sm text-slate-500 hover:text-blue-600 transition-colors" data-testid="forgot-password-link">
              Forgot your password?
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image/Graphic */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-blue-50 to-slate-50 items-center justify-center p-12">
        <div className="max-w-lg text-center">
          <div className="w-64 h-64 mx-auto mb-8 bg-white rounded-2xl shadow-xl flex items-center justify-center">
            <div className="relative">
              <div className="w-40 h-40 bg-blue-100 rounded-full flex items-center justify-center">
                <Briefcase className="w-20 h-20 text-blue-600" />
              </div>
              <div className="absolute -top-2 -right-2 w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-white text-xl font-bold">✓</span>
              </div>
            </div>
          </div>
          <h3 className="font-outfit text-2xl font-bold text-slate-900 mb-4">
            Your Training Partner
          </h3>
          <p className="text-slate-500">
            Access thousands of expert trainers or find your next training opportunity
          </p>
        </div>
      </div>
    </div>
  );
}
